// Thin adapter. The implementation now lives in @intrasync/stores
// (specialized/ChannelStore); this file exists only to preserve the local import
// path 'stores/ChannelStore', the local default export, the local type exports,
// and the local RootStore constructor type.
//
// Why a cast and not `class ChannelStore extends LibChannelStore`: the library
// store calls makeAutoObservable(this) in its constructor, and MobX throws
// ("can only be used for classes that don't have a superclass") when the
// instance has one. A subclass shim would crash at startup.
//
// Why the bare '@intrasync/stores' specifier: tsconfig.json sets
// "moduleResolution": "Node", which ignores the package `exports` map, so
// '@intrasync/stores/specialized/ChannelStore' does not resolve for TypeScript.
// The root barrel re-exports ChannelStore, so this does.
import { ChannelStore as LibChannelStore } from '@intrasync/stores';
import type RootStore from './RootStore';

// Re-declared locally: the library exports these from its
// specialized/ChannelStore module, which is not reachable from the bare entry
// under this tsconfig. Structurally identical to the library's definitions.
export interface ChannelServiceResponse {
  data?: ChannelItem[];
  result?: {
    data?: ChannelItem[];
  };
}

export interface UpdatedChannel {
  connectionId?: string;
  routeName: string;
  reloadApp?: boolean;
  params?: Record<string, unknown>;
  queryParams?: Record<string, unknown>;
}

export interface ChannelItem {
  connectionId: string;
  routeChannels?: string[];
  appName?: string;
  appVersion?: string;
  userId?: number;
  deviceId?: string;
  loggedIn: boolean;
  createdDate?: string;
  createdTime?: string;
  updatedDate?: string;
  updatedTime?: string;
}

interface ChannelStoreOptions {
  debugMode: boolean;
  disableCreatedListener?: boolean;
  disableUpdatedListener?: boolean;
  disablePatchedListener?: boolean;
  disableRemovedListener?: boolean;
}

/**
 * The library types its stores against ITS OWN RootStore, which declares
 * `private` members and is therefore nominal to TypeScript. This app mounts its
 * local RootStore, which is structurally compatible at runtime (the store reads
 * only rootStore.app, .authStore, .routerStore, .viewStore and .mode). Re-type
 * the constructor here so no call site has to cast.
 */
type ChannelStoreConstructor = new (
  rootStore: RootStore,
  options: ChannelStoreOptions
) => LibChannelStore;

const ChannelStore = LibChannelStore as unknown as ChannelStoreConstructor;
type ChannelStore = LibChannelStore;

export default ChannelStore;
